import { DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LocalizationService } from '@abp/ng.core';
import { Confirmation } from '@abp/ng.theme.shared';

import { IWizardStepDefinition } from '../interfaces/IWizard';
import { localizeWithFallback } from '../../utils/localization-utils';
import { IModal } from '../../zn-modal-wrapper/interfaces/IModal';
import { ModalBuilder } from '../../zn-modal-wrapper/services/modal-builder';
import { ZnStepperWizard } from '../zn-stepper-wizard';
import { ZnConfirmation } from '../../services/zn-confirmation';

type WizardStepComponentType = IWizardStepDefinition['component'];

interface BaseStepDefinition {
  id: number;
  component: WizardStepComponentType;
}

interface PlainStepDefinition extends BaseStepDefinition {
  title: string;
}

interface LocalizedStepDefinition extends BaseStepDefinition {
  titleKey: string;
}

export interface WizardCancelConfirmationOptions {
  messageKey: string;
  titleKey: string;
}

const DEFAULT_WIZARD_CANCEL_CONFIRMATION_OPTIONS: WizardCancelConfirmationOptions = {
  messageKey: 'LanguageService::ConfirmCancelChangesHarvestPreparation',
  titleKey: 'LanguageService::ConfirmCancelChangesTitleHarvestPreparation',
};

export const buildStepDefinitions = (
  steps: readonly PlainStepDefinition[],
): IWizardStepDefinition[] =>
  steps.map(step => ({
    id: step.id,
    title: step.title,
    component: step.component,
  }));

export const buildLocalizedStepDefinitions = (
  localizationService: LocalizationService,
  localizationKeyPrefix: string,
  steps: readonly LocalizedStepDefinition[],
): IWizardStepDefinition[] =>
  steps.map(step => ({
    id: step.id,
    title: localizeWithFallback(
      localizationService,
      `${localizationKeyPrefix}.${step.titleKey}`,
    ),
    component: step.component,
  }));

export const hasWizardPendingChanges = (
  wizard: { hasPendingChanges(): boolean } | undefined,
): boolean => wizard?.hasPendingChanges() ?? false;

export const showCancelConfirmationOrRedirect = (
  confirmationService: ZnConfirmation,
  hasPendingChanges: boolean,
  onConfirm: () => void,
  options?: WizardCancelConfirmationOptions,
): void => {
  if (!hasPendingChanges) {
    onConfirm();
    return;
  }

  const resolvedOptions = options ?? DEFAULT_WIZARD_CANCEL_CONFIRMATION_OPTIONS;

  confirmationService
    .showCancelConfirmation({
      messageKey: resolvedOptions.messageKey,
      titleKey: resolvedOptions.titleKey,
    })
    .subscribe(status => {
      if (status === Confirmation.Status.confirm) {
        onConfirm();
      }
    });
};

export const redirectToHome = async (router: Router): Promise<void> => {
  await router.navigateByUrl('/');
};

export abstract class ZnWizardHost {
  private readonly confirmationService = inject(ZnConfirmation);
  private readonly localizationService = inject(LocalizationService);
  private readonly modalBuilder = inject(ModalBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  protected readonly successModalVisible = signal(false);
  protected stepDefinitions: IWizardStepDefinition[] = [];
  protected successMessageKey = '';
  private completionMessage?: IModal;

  protected abstract getWizard(): ZnStepperWizard | undefined;

  protected onBeforeRedirect(): void {}

  protected getCancelConfirmationOptions(): WizardCancelConfirmationOptions {
    return {
      messageKey: 'LanguageService::ConfirmCancelChangesHarvestPreparation',
      titleKey: 'LanguageService::ConfirmCancelChangesTitleHarvestPreparation',
    };
  }

  protected initializeStepperWizard(
    localizationKeyPrefix: string,
    steps: readonly LocalizedStepDefinition[],
    completionMessage: IModal,
    successMessageKey: string,
  ): void {
    this.stepDefinitions = buildLocalizedStepDefinitions(
      this.localizationService,
      localizationKeyPrefix,
      steps,
    );
    this.completionMessage = completionMessage;
    this.successMessageKey = successMessageKey;
  }

  protected onSubmitWizard(_data: Record<number, unknown>): void {
    if (!this.completionMessage) {
      return;
    }

    const modalRef = this.modalBuilder.openModal(
      this.completionMessage.component,
      this.completionMessage,
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirm: boolean) => {
        if (confirm) {
          this.successModalVisible.set(true);
        }
      });
  }

  protected onCancel(): void {
    showCancelConfirmationOrRedirect(
      this.confirmationService,
      this.hasPendingChanges(),
      () => {
        this.onBeforeRedirect();
        void redirectToHome(this.router);
      },
      this.getCancelConfirmationOptions(),
    );
  }

  protected hasPendingChanges(): boolean {
    return hasWizardPendingChanges(this.getWizard());
  }

  protected async onSuccessModalVisibleChange(isVisible: boolean): Promise<void> {
    this.successModalVisible.set(isVisible);
    if (!isVisible) {
      this.onBeforeRedirect();
      await redirectToHome(this.router);
    }
  }
}
